import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import type { CurrentUserDto, DevelopmentLoginOptionDto, UserRole } from '@/api/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';
import { resolveLoginReturnTo } from '@/lib/authNavigation';
import styles from '@/components/auth/AuthPages.module.css';

export { resolveLoginReturnTo } from '@/lib/authNavigation';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<'credentials' | UserRole | null>(null);
  const [developmentAccounts, setDevelopmentAccounts] = useState<DevelopmentLoginOptionDto[]>([]);
  const { login, developmentLogin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    let active = true;
    void authApi.developmentLoginOptions()
      .then((accounts) => {
        if (active) setDevelopmentAccounts(accounts);
      })
      .catch(() => {
        if (active) setDevelopmentAccounts([]);
      });

    return () => {
      active = false;
    };
  }, []);

  function finishLogin(user: CurrentUserDto) {
    const returnTo = resolveLoginReturnTo(
      (location.state as { from?: string } | null)?.from,
      location.search,
    );
    if (user.mustChangePassword) {
      navigate('/change-password', { replace: true });
    } else {
      navigate(returnTo, { replace: true });
    }
  }

  async function runLogin(action: 'credentials' | UserRole, authenticate: () => Promise<CurrentUserDto>) {
    setError(null);
    setSubmitting(action);
    try {
      finishLogin(await authenticate());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to sign in.');
    } finally {
      setSubmitting(null);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void runLogin('credentials', () => login(email, password));
  }

  function handleDevelopmentLogin(role: UserRole) {
    void runLogin(role, () => developmentLogin(role));
  }

  return (
    <main className={styles.authPage}>
      <div className={styles.authContent}>
        <div className={styles.brand}>MegaTraderDX</div>
        <Card header="Sign in">
          <form className={styles.form} onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}
            <Input
              label="Email"
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button className={styles.submit} type="submit" disabled={submitting !== null}>
              {submitting === 'credentials' ? 'Signing in...' : 'Sign in'}
            </Button>
            {import.meta.env.DEV && developmentAccounts.length > 0 && (
              <DevelopmentLoginButtons
                accounts={developmentAccounts}
                submitting={submitting}
                onLogin={handleDevelopmentLogin}
              />
            )}
          </form>
        </Card>
      </div>
    </main>
  );
}

interface DevelopmentLoginButtonsProps {
  accounts: DevelopmentLoginOptionDto[];
  submitting: 'credentials' | UserRole | null;
  onLogin: (role: UserRole) => void;
}

export function DevelopmentLoginButtons({
  accounts,
  submitting,
  onLogin,
}: DevelopmentLoginButtonsProps) {
  return (
    <section className={styles.developmentLogin} aria-label="Development login">
      <div className={styles.developmentDivider}>Development</div>
      <div className={styles.developmentActions}>
        {accounts.map((account) => (
          <Button
            key={account.role}
            type="button"
            variant={account.role === 'Admin' ? 'outline-success' : 'outline-info'}
            title={`${account.displayName} (${account.email})`}
            disabled={submitting !== null}
            onClick={() => onLogin(account.role)}
          >
            {submitting === account.role ? 'Signing in...' : `Login as ${account.role}`}
          </Button>
        ))}
      </div>
    </section>
  );
}
